import { graphql } from '@web/graphql-queries/gql'
import useGraphQLClient from './hooks/useGraphQLClient'
import { useMutation } from '@tanstack/react-query'
import { BASE_PAYMENT_INTENT } from '../components/PageComponent/DonationForm/utils'
import { DonationFrequency } from '@web/graphql-components'
import {
  CreatePaymentIntentMutation,
  CreateSubscriptionMutation,
  InstallmentInput,
  PaymentMethodType,
  UpdatePaymentDetailsInput,
  UpdatePaymentInput,
  UpdatePaymentIntentDetailsMutation,
  UpdatePaymentIntentMutation,
  UpdateSubscriptionDetailsMutation,
  UpdateSubscriptionMutation,
} from '@web/graphql-queries/graphql'
import { usePageStore } from './hooks/usePageStore'
import { getFacebookPixelCookies, generateOrGetDeviceUUID } from '../utils/analytics'

const createPaymentIntentDocument = graphql(/* GraphQL */ `
  mutation CreatePaymentIntent($input: CreatePaymentIntentInput!) {
    createPaymentIntent(input: $input) {
      clientSecret
      paymentTransactionId
      paymentIntentId
    }
  }
`)

const createSubscriptionDocument = graphql(/* GraphQL */ `
  mutation CreateSubscription($input: CreateSubscriptionInput!) {
    createSubscription(input: $input) {
      clientSecret
      subscriptionId
      paymentSubscriptionId
    }
  }
`)

type PaymentIntentFormInput = {
  donationFormAmount: number
  email: string
  paymentMethodType: PaymentMethodType
  isDonorChooseToPayFee?: boolean
  donorPaysAmount?: number
  givingFrequency?: DonationFrequency
  installment?: InstallmentInput
  eventId?: string
}

export const useCreatePaymentObjectMutation = () => {
  const { charityId, campaignId, pageId, pageBucketId, isLiveMode, profileId, profileData } = usePageStore.getState()

  const graphQLClient = useGraphQLClient()
  return useMutation({
    mutationKey: ['createPaymentObject'],
    mutationFn: async (input: PaymentIntentFormInput) => {
      const { fbp, fbc } = getFacebookPixelCookies()
      const deviceUUID = generateOrGetDeviceUUID()
      const { givingFrequency, installment, ...formInput } = input
      const paymentInput = {
        ...BASE_PAYMENT_INTENT,
        charityId,
        campaignId,
        pageId,
        pageBucketId,
        profileId,
        profileName: profileData?.name,
        firstName: '',
        lastName: '',
        donationPageUrl: window.location.href.slice(0, 500), // max object length in stripe is 500 characters
        isLiveMode,
        fbp,
        fbc,
        deviceUUID,
        ...formInput,
      } as PaymentIntentFormInput

      try {
        if (givingFrequency === DonationFrequency.OneTime) {
          const res = await graphQLClient<CreatePaymentIntentMutation>({
            document: createPaymentIntentDocument,
            options: { input: paymentInput },
          })

          return {
            clientSecret: res.createPaymentIntent.clientSecret,
            paymentTransactionId: res.createPaymentIntent.paymentTransactionId,
            paymentIntentId: res.createPaymentIntent.paymentIntentId,
          }
        }

        const res = await graphQLClient<CreateSubscriptionMutation>({
          document: createSubscriptionDocument,
          options: {
            input: {
              ...paymentInput,
              givingFrequency,
              ...(installment
                ? {
                    donationFormAmount: Math.floor(input.donationFormAmount / installment.totalNumberOfInstallments),
                    installment: {
                      ...installment,
                      totalAmount: input.donationFormAmount,
                    },
                  }
                : {}),
            },
          },
        })

        return {
          clientSecret: res.createSubscription.clientSecret,
          subscriptionId: res.createSubscription.subscriptionId,
          paymentSubscriptionId: res.createSubscription.paymentSubscriptionId,
        }
      } catch (e) {
        throw new Error(e)
      }
    },
  })
}

const updatePaymentIntentDocument = graphql(/* GraphQL */ `
  mutation UpdatePaymentIntent($input: UpdatePaymentInput!) {
    updatePaymentIntent(input: $input) {
      clientSecret
      paymentIntentId
    }
  }
`)

const updateSubscriptionDocument = graphql(/* GraphQL */ `
  mutation UpdateSubscription($input: UpdatePaymentInput!) {
    updateSubscription(input: $input) {
      clientSecret
      subscriptionId
    }
  }
`)

export const useUpdatePaymentObjectMutation = () => {
  const charityId = usePageStore((state) => state.charityId)
  const campaignId = usePageStore((state) => state.campaignId)
  const pageId = usePageStore((state) => state.pageId)
  const isLiveMode = usePageStore((state) => state.isLiveMode)

  const graphQLClient = useGraphQLClient()

  return useMutation({
    mutationFn: async (input: Partial<UpdatePaymentInput>) => {
      try {
        if (input.givingFrequency === DonationFrequency.OneTime) {
          const res = await graphQLClient<UpdatePaymentIntentMutation>({
            document: updatePaymentIntentDocument,
            options: {
              input: { ...input, charityId, campaignId, pageId, isLiveMode },
            },
          })
          return {
            clientSecret: res.updatePaymentIntent.clientSecret,
          }
        }

        const res = await graphQLClient<UpdateSubscriptionMutation>({
          document: updateSubscriptionDocument,
          options: {
            input: { ...input, charityId, campaignId, pageId, isLiveMode },
          },
        })

        return {
          clientSecret: res.updateSubscription.clientSecret,
        }
      } catch (e) {
        throw new Error(e)
      }
    },
  })
}

const updatePaymentIntentDetailsDocument = graphql(`
  mutation UpdatePaymentIntentDetails($input: UpdatePaymentDetailsInput!) {
    updatePaymentIntentDetails(input: $input) {
      paymentIntentId
      clientSecret
    }
  }
`)

const updateSubscriptionDetailsDocument = graphql(`
  mutation UpdateSubscriptionDetails($input: UpdatePaymentDetailsInput!) {
    updateSubscriptionDetails(input: $input) {
      subscriptionId
      clientSecret
    }
  }
`)

export const useUpdatePaymentObjectDetailsMutation = () => {
  const graphQLClient = useGraphQLClient()
  const isLiveMode = usePageStore((state) => state.isLiveMode)

  return useMutation({
    mutationFn: async (input: UpdatePaymentDetailsInput) => {
      try {
        if (!input.givingFrequency || input.givingFrequency !== DonationFrequency.OneTime) {
          const res = await graphQLClient<UpdateSubscriptionDetailsMutation>({
            document: updateSubscriptionDetailsDocument,
            options: {
              input: { ...input, isLiveMode },
            },
          })

          return { clientSecret: res.updateSubscriptionDetails.clientSecret }
        }

        await graphQLClient<UpdatePaymentIntentDetailsMutation>({
          document: updatePaymentIntentDetailsDocument,
          options: {
            input: { ...input, isLiveMode },
          },
        })

        return { clientSecret: null }
      } catch (e) {
        throw new Error(e)
      }
    },
  })
}
